import { Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";

/**
 * Marks an invalid reactive form fully touched/dirty and scrolls the first invalid
 * control into view (focusing it) so the user sees exactly what to fix. Lets a
 * "Next"/submit button stay enabled and validate on click, instead of being disabled
 * while the form is invalid.
 */
@Injectable()
export class FormValidationService {
  /**
   * @param form control tree to mark as touched/dirty.
   * @param root DOM subtree to search for the invalid control (defaults to `document`).
   *   Pass the component host so only the current form/step is considered — the app
   *   controls (`app-input`, `app-dropdown`, ...) render no wrapping `<form>` element.
   */
  scrollToFirstInvalidControl(form: FormGroup | FormArray, root: ParentNode = document): void {
    this.markAllAsTouched(form);

    const target = this.findFirstInvalidElement(root);
    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    // preventScroll so focusing the field doesn't fight the smooth scroll above.
    this.resolveFocusable(target)?.focus({ preventScroll: true });
  }

  /** Innermost invalid element in document order (skips the wrapping form-group container). */
  private findFirstInvalidElement(root: ParentNode): HTMLElement | null {
    const invalid = Array.from(root.querySelectorAll<HTMLElement>(".ng-invalid"));
    if (!invalid.length) {
      return null;
    }
    // Prefer a leaf control (one with no nested .ng-invalid) so we land on the field
    // itself, not the [formGroup] wrapper that is also flagged invalid.
    return invalid.find((el) => !el.querySelector(".ng-invalid")) ?? invalid[0];
  }

  private resolveFocusable(element: HTMLElement): HTMLElement | null {
    const selector = "input, select, textarea, [tabindex]";
    return element.matches(selector) ? element : element.querySelector<HTMLElement>(selector);
  }

  private markAllAsTouched(group: FormGroup | FormArray): void {
    Object.values(group.controls).forEach((control: AbstractControl) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }
}
